/**
 * App-wide React ErrorBoundary.
 *
 * Catches any rendering error inside a route subtree and renders a
 * friendly fallback message instead of leaving a blank screen.  Class
 * component is required because `componentDidCatch` and
 * `getDerivedStateFromError` are class-only React APIs.
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Surface the crash to the dev console so it's still
    // diagnosable; in production this also feeds source-mapped
    // stack traces into the platform's rrweb recorder.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "Something went wrong rendering this page.";
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background text-foreground p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6 sm:p-8 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-rose-400" />
          <h1 className="font-display text-2xl font-bold mb-2">
            Something broke on this page.
          </h1>
          <p className="text-sm text-muted-foreground mb-1">
            We hit an unexpected rendering error and stopped the page so the
            rest of the app stays usable. Our team has been notified.
          </p>
          <p
            className="text-[11px] font-mono text-rose-300/80 break-words bg-rose-500/10 border border-rose-500/20 rounded-md px-2 py-1 mt-3 mb-5"
            data-testid="error-boundary-message"
          >
            {msg}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReset}
              data-testid="error-boundary-retry-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-foreground text-background text-xs font-bold uppercase tracking-[0.14em] hover:opacity-90 transition-opacity"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border border-border text-xs font-bold uppercase tracking-[0.14em] hover:bg-card transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
